import React, { useEffect } from 'react'
import moment from 'moment'
import { Pagination } from 'transfix-ui/components/Pagination'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { ITrailer } from 'types/FMSGraphqlTypes'
import { ITrailerStatesEnum } from 'types/graphqlTypes'
import { FORMATS } from 'constants/datetime'
import { getLocation, getLoadId, getArrivedAt, getArrivedAtNote } from '../../../utils/trailers'
import { useTrailers, ITEMS_PER_PAGE } from '../hooks/useTrailers'
import TrailerItem from './TrailerItem'

interface ITrailerListParams {
  /**
   * Carrier id to send as argument in trailer query
   * @example "1"
   */
  selectedCarrierId?: string
  /**
   * Boolean used to determine which trailer states to send as argument in trailer query
   * @example true
   */
  showInactiveAndUnassigned?: boolean
  /**
   * Comma separated string containing trailer numbers to search
   * @example "123,456"
   */
  trailerSearchInput?: string
}

const useStyles = makeStyles(() => ({
  pagination: {
    '& > div': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
}))

const ALL_TRAILER_STATES = Object.values(ITrailerStatesEnum)
const REDUCED_TRAILER_STATES = Object.values(ITrailerStatesEnum).filter(
  item => ![ITrailerStatesEnum.inactive, ITrailerStatesEnum.unassigned].includes(item)
)

const TrailerList = ({
  selectedCarrierId = '',
  showInactiveAndUnassigned = false,
  trailerSearchInput = '',
}: ITrailerListParams) => {
  const classes = useStyles()

  const {
    endCursor,
    loading,
    pageNumber,
    setFilters,
    setPageNumber,
    startCursor,
    totalCount,
    trailers = [],
  } = useTrailers({
    filters: {
      carrierId: selectedCarrierId?.toString() || '',
      states: showInactiveAndUnassigned ? ALL_TRAILER_STATES : REDUCED_TRAILER_STATES,
    },
  })

  const handlePageChange = (newPageNumber: number) => {
    setPageNumber(newPageNumber)
    if (newPageNumber < pageNumber) {
      setFilters(prevState => ({
        ...prevState,
        after: undefined,
        before: startCursor || '',
      }))
    } else if (newPageNumber > pageNumber) {
      setFilters(prevState => ({
        ...prevState,
        after: endCursor || '',
        before: undefined,
      }))
    }
  }

  // Update filters on hook when filters have been updated in UI, also resets pagination
  useEffect(() => {
    setFilters(prevState => ({
      ...prevState,
      carrierId: selectedCarrierId,
      states: showInactiveAndUnassigned ? ALL_TRAILER_STATES : REDUCED_TRAILER_STATES,
      after: undefined,
      before: undefined,
      first: ITEMS_PER_PAGE,
      searchTerm: trailerSearchInput,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCarrierId, showInactiveAndUnassigned, trailerSearchInput])

  if (loading) {
    return <Spinner />
  }

  if (!trailers || !trailers.length) {
    return <Box>No trailers were found.</Box>
  }

  return (
    <Box>
      {((trailers as ITrailer[]) || []).map(item => {
        const currentMoment = moment(moment())
        const arrivedAt = getArrivedAt(
          item.shipmentDetails,
          item.arrivedAt,
          item.facility?.timezone
        )
        const arrivedAtMoment = moment(arrivedAt)
        const arrivedAtNote = getArrivedAtNote(
          currentMoment,
          arrivedAtMoment,
          item.state as ITrailerStatesEnum,
          item.shipmentDetails?.state
        )
        return (
          <TrailerItem
            key={item.id}
            id={item.id}
            number={item.number}
            status={item.state as ITrailerStatesEnum}
            loadId={getLoadId(item.shipmentDetails?.id)}
            location={getLocation(item.facility?.name || '', item.shipmentDetails?.state)}
            note={item.note || undefined}
            arrivedAt={
              arrivedAtMoment.isValid()
                ? arrivedAtMoment.format(FORMATS.trailerArrivedAtFormat)
                : '—'
            }
            selectedCarrierId={selectedCarrierId}
            arrivedAtNote={arrivedAtNote}
          />
        )
      })}
      {totalCount > ITEMS_PER_PAGE && (
        <Box className={classes.pagination}>
          <Pagination
            currentPage={pageNumber}
            numberOfPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
            displayPageNumbers={false}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  )
}

export default TrailerList
